import { type ColumnDef } from "@tanstack/react-table";
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CheckCircle2,
  Copy,
  Eye,
  Loader2,
} from "lucide-react";
import { useMemo } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { useSyncPatientByEhr } from "@elation/api-client/src/stitch-web";
import type {
  EhrConnector,
  PatientSearchConnectorError,
  PatientSearchItem,
  PatientSearchResultErrors,
} from "@elation/api-client/src/stitch-web/models";
import { SearchPatientsSortBy } from "@elation/api-client/src/stitch-web/models/searchPatientsSortBy";

import { HeaderText } from "@src/shared/components/general/HeaderText";
import { Text } from "@src/shared/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Badge } from "@src/shared/components/ui/badge";
import { Button } from "@src/shared/components/ui/button";
import { DataTable } from "@src/shared/components/ui/data-table";
import { Skeleton } from "@src/shared/components/ui/skeleton";
import { cn } from "@src/shared/lib/utils";
import { navigation } from "@src/shared/navigation";
import { getAssetUrl } from "@src/shared/utils/assets/getAssetUrl";
import { captureException } from "@src/shared/utils/observability/sentry";

export type PatientSortField = SearchPatientsSortBy;
export type PatientSortOrder = "asc" | "desc";

export type PatientSearchPatient = PatientSearchItem;
export type PatientSearchErrors = PatientSearchResultErrors;

function getPatientSearchPatientKey(patient: PatientSearchPatient) {
  if (patient.patientId != null) {
    return `internal:${patient.patientId}`;
  }

  if (patient.ehr && patient.externalPatientId != null) {
    return `${patient.ehr}:${patient.externalPatientId}`;
  }

  return null;
}

export interface PatientSearchProps {
  patients: PatientSearchPatient[];
  searchErrors?: PatientSearchErrors;
  isLoading?: boolean;
  /** True when the search request failed (network / 5xx / non-recoverable 4xx). */
  isError?: boolean;

  /** Active server sort field. Omit for a non-interactive table. */
  sortBy?: PatientSortField;
  /** Active server sort order. */
  order?: PatientSortOrder;
  /** Called when the user clicks a server-sortable column header. Omit to hide sort affordances. */
  onSortChange?: (sortBy: PatientSortField, order: PatientSortOrder) => void;

  /** True when the server returned a nextCursor for the current page. */
  hasNextPage?: boolean;
  /** True when there is a previous page on the client's cursor stack. */
  hasPrevPage?: boolean;
  onNextPage?: () => void;
  onPrevPage?: () => void;
}

type PatientRow = PatientSearchPatient & { fullName: string };

const EHR_LABELS: Record<EhrConnector, string> = {
  elation: "Elation",
  athenahealth: "Athenahealth",
  eclinicalworks: "eClinicalWorks",
  redoxfhirsandbox: "Redox FHIR Sandbox",
};

const EHR_LOGOS: Record<EhrConnector, { src: string; className: string }> = {
  elation: { src: "/images/elation-logo.svg", className: "h-5" },
  athenahealth: { src: "/images/athena-logo.svg", className: "h-4" },
  eclinicalworks: { src: "/images/eclinical-works-logo.svg", className: "h-3" },
  redoxfhirsandbox: { src: "/images/redox-logo-sandbox.svg", className: "h-4" },
};

const EHR_SYNC_TOAST_DELAY_MS = 300;

async function copySearchErrorRequestId(requestId: string) {
  try {
    await navigator.clipboard.writeText(requestId);
    toast.success("Copied request ID");
  } catch {
    toast.error("Couldn't copy request ID");
  }
}

function nextOrder(
  current: { sortBy: PatientSortField; order: PatientSortOrder },
  clicked: PatientSortField,
): PatientSortOrder {
  if (current.sortBy !== clicked) return "asc";
  return current.order === "asc" ? "desc" : "asc";
}

function SortIcon({ state }: { state: "asc" | "desc" | false }) {
  if (state === "asc") return <ArrowUp className="ml-1 size-3.5" />;
  if (state === "desc") return <ArrowDown className="ml-1 size-3.5" />;
  return <ArrowUpDown className="ml-1 size-3.5" />;
}

function PatientSearchErrorAlert({
  ehr,
  error,
}: {
  ehr: EhrConnector;
  error: PatientSearchConnectorError;
}) {
  const ehrLabel = EHR_LABELS[ehr] ?? ehr;

  return (
    <HorizontalFlexbox
      alignItems="flex-start"
      gap="xs"
      className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-destructive"
    >
      <AlertCircle className="mt-0.5 size-4 shrink-0" />
      <VerticalFlexbox gap="xs" className="min-w-0">
        <Text sizing="sm">
          {ehrLabel}: {error.message}
        </Text>
        {error.requestId ? (
          <HorizontalFlexbox
            alignItems="center"
            gap="xs"
            flexWrap="wrap"
            className="rounded-md border border-destructive/20 bg-background/80 px-2 py-1 text-foreground"
          >
            <Text sizing="xs" appearance="subdued" fontWeight={600}>
              Request ID
            </Text>
            <code className="break-all font-mono text-xs text-foreground">
              {error.requestId}
            </code>
            <button
              type="button"
              aria-label={`Copy ${ehrLabel} request ID`}
              onClick={() => void copySearchErrorRequestId(error.requestId)}
              className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <Copy className="size-3.5" />
            </button>
          </HorizontalFlexbox>
        ) : null}
      </VerticalFlexbox>
    </HorizontalFlexbox>
  );
}

function PatientSearchRowActions({
  patient,
}: {
  patient: PatientSearchPatient;
}) {
  const navigate = useNavigate();
  const syncPatientByEhr = useSyncPatientByEhr();
  const patientKey = getPatientSearchPatientKey(patient);

  const handleView = async () => {
    if (patient.patientId != null) {
      navigate(navigation.patientDetail(String(patient.patientId)), {
        state: { source: "patient list" },
      });
      return;
    }

    if (!patient.ehr || patient.externalPatientId == null) {
      toast.error("Unable to open this patient.");
      return;
    }

    const ehr = patient.ehr;
    let loadingToastId: string | number | undefined;

    const loadingToastTimer = window.setTimeout(() => {
      loadingToastId = toast.loading(
        `Downloading this patient from ${EHR_LABELS[ehr]}...`,
      );
    }, EHR_SYNC_TOAST_DELAY_MS);

    try {
      const result = await syncPatientByEhr.mutateAsync({
        ehr,
        patientId: String(patient.externalPatientId),
      });

      const patientId = result.data.patient.id;

      navigate(navigation.patientDetail(String(patientId)), {
        state: { source: "patient list" },
      });
    } catch (error) {
      captureException(error, {
        tags: { feature: "patient-search", action: "sync-by-ehr" },
        extra: { ehr, externalPatientId: patient.externalPatientId },
      });
      toast.error("Unable to sync this patient. Please try again.");
    } finally {
      window.clearTimeout(loadingToastTimer);

      if (loadingToastId != null) {
        toast.dismiss(loadingToastId);
      }
    }
  };

  return (
    <div className="flex justify-end">
      <Button
        size="sm"
        className="bg-accent text-accent-foreground hover:bg-accent/90"
        onClick={(event) => {
          event.stopPropagation();
          if (patientKey != null && !syncPatientByEhr.isPending) {
            void handleView();
          }
        }}
        disabled={patientKey == null || syncPatientByEhr.isPending}
      >
        {syncPatientByEhr.isPending ? (
          <Loader2 className="mr-1 size-3.5 animate-spin" />
        ) : (
          <Eye className="mr-1 size-3.5" />
        )}
        {syncPatientByEhr.isPending ? "Syncing..." : "View"}
      </Button>
    </div>
  );
}

export const PatientSearch = ({
  patients,
  searchErrors = {},
  isLoading = false,
  isError = false,
  sortBy,
  order,
  onSortChange,
  hasNextPage = false,
  hasPrevPage = false,
  onNextPage,
  onPrevPage,
}: PatientSearchProps) => {
  const serverSortEnabled = Boolean(onSortChange && sortBy && order);
  const searchErrorEntries = Object.entries(searchErrors) as Array<
    [EhrConnector, PatientSearchConnectorError]
  >;
  const rows = useMemo<PatientRow[]>(
    () =>
      patients.map((patient) => ({
        ...patient,
        fullName: `${patient.firstName ?? ""} ${patient.lastName ?? ""}`.trim(),
      })),
    [patients],
  );

  const columns = useMemo<ColumnDef<PatientRow>[]>(() => {
    const serverSortHeader =
      (
        label: string,
        field: PatientSortField,
      ): ColumnDef<PatientRow>["header"] =>
      () => {
        if (!serverSortEnabled || !onSortChange || !sortBy || !order) {
          return label;
        }
        const state = sortBy === field ? order : false;
        return (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 px-2 text-muted-foreground"
            onClick={() =>
              onSortChange(field, nextOrder({ sortBy, order }, field))
            }
          >
            {label}
            <SortIcon state={state} />
          </Button>
        );
      };

    return [
      {
        accessorKey: "fullName",
        enableSorting: false,
        header: serverSortHeader("Name", "lastName"),
        cell: ({ row }) => (
          <Text fontWeight={500}>{row.original.fullName || "—"}</Text>
        ),
      },
      {
        accessorKey: "ehr",
        enableSorting: false,
        header: "EHR",
        cell: ({ row }) => {
          const { ehr, syncStatus } = row.original;
          const logo = ehr ? EHR_LOGOS[ehr] : undefined;

          if (!ehr || !logo) {
            return (
              <Text sizing="sm" appearance="subdued">
                —
              </Text>
            );
          }

          return (
            <HorizontalFlexbox alignItems="center" gap="xs">
              <div className="flex h-6 items-center">
                <img
                  src={getAssetUrl(logo.src)}
                  alt={EHR_LABELS[ehr] ?? ehr}
                  className={cn(
                    "w-auto max-w-[120px] object-contain object-left",
                    logo.className,
                  )}
                />
              </div>
              {syncStatus === "synced" ? (
                <Badge
                  variant="secondary"
                  className="gap-1 border-emerald-200 bg-emerald-50 text-emerald-800"
                >
                  <CheckCircle2 className="size-3" />
                  Synced
                </Badge>
              ) : null}
              {syncStatus === "not_synced" ? (
                <Badge
                  variant="secondary"
                  className="border-amber-200 bg-amber-50 text-amber-900"
                >
                  Not synced
                </Badge>
              ) : null}
            </HorizontalFlexbox>
          );
        },
      },
      {
        accessorKey: "dob",
        enableSorting: false,
        header: "Date of Birth",
        cell: ({ row }) => (
          <Text sizing="sm" appearance="subdued">
            {row.original.dob || "—"}
          </Text>
        ),
      },
      {
        accessorKey: "sex",
        enableSorting: false,
        header: "Gender",
        cell: ({ row }) => (
          <Text sizing="sm" appearance="subdued">
            {row.original.sex ?? "—"}
          </Text>
        ),
      },
      {
        id: "address",
        enableSorting: false,
        header: "Address",
        accessorFn: (patient) =>
          [patient.addressLine1, patient.addressLine2]
            .filter(Boolean)
            .join(", "),
        cell: ({ row }) => (
          <Text sizing="sm" appearance="subdued">
            {[row.original.addressLine1, row.original.addressLine2]
              .filter(Boolean)
              .join(", ") || "—"}
          </Text>
        ),
      },
      {
        accessorKey: "city",
        enableSorting: false,
        header: "City",
        cell: ({ row }) => (
          <Text sizing="sm" appearance="subdued">
            {row.original.city ?? "—"}
          </Text>
        ),
      },
      {
        accessorKey: "state",
        enableSorting: false,
        header: "State",
        cell: ({ row }) => (
          <Text sizing="sm" appearance="subdued">
            {row.original.state ?? "—"}
          </Text>
        ),
      },
      {
        accessorKey: "zip",
        enableSorting: false,
        header: "Zip Code",
        cell: ({ row }) => (
          <Text sizing="sm" appearance="subdued">
            {row.original.zip ?? "—"}
          </Text>
        ),
      },
      {
        id: "actions",
        enableHiding: false,
        enableSorting: false,
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => <PatientSearchRowActions patient={row.original} />,
      },
    ];
  }, [onSortChange, sortBy, order, serverSortEnabled]);

  if (isLoading) {
    return (
      <VerticalFlexbox
        gap="sm"
        className="rounded-xl border border-border bg-card p-4 shadow-soft sm-stitch:p-6"
      >
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </VerticalFlexbox>
    );
  }

  if (isError) {
    return (
      <VerticalFlexbox
        alignItems="center"
        justifyContent="center"
        gap="xs"
        className="rounded-xl border border-destructive/50 bg-destructive/5 p-6 text-center sm-stitch:p-10"
      >
        <AlertCircle className="size-6 text-destructive" />
        <HeaderText tag="h3" fontWeight={600} className="text-base">
          Unable to load patients
        </HeaderText>
        <Text sizing="sm" appearance="subdued" className="max-w-md">
          Something went wrong retrieving your roster. Adjust your search or try
          again in a moment.
        </Text>
      </VerticalFlexbox>
    );
  }

  const ehrSearchErrors =
    searchErrorEntries.length > 0 ? (
      <VerticalFlexbox gap="xs">
        {searchErrorEntries.map(([ehr, error]) => (
          <PatientSearchErrorAlert key={ehr} ehr={ehr} error={error} />
        ))}
      </VerticalFlexbox>
    ) : null;

  if (patients.length === 0) {
    return (
      <VerticalFlexbox gap="sm">
        {ehrSearchErrors}
        <VerticalFlexbox
          alignItems="center"
          justifyContent="center"
          gap="xs"
          className="rounded-xl border border-dashed border-border bg-muted/40 p-6 text-center sm-stitch:p-10"
        >
          <HeaderText tag="h3" fontWeight={600} className="text-base">
            No patients found
          </HeaderText>
          <Text sizing="sm" appearance="subdued" className="max-w-md">
            Adjust your search filters above to find a matching patient record.
          </Text>
        </VerticalFlexbox>
      </VerticalFlexbox>
    );
  }

  return (
    <VerticalFlexbox gap="sm">
      <VerticalFlexbox gap="xs">
        <HeaderText tag="h2" fontWeight={600} className="text-xl">
          Select a patient
        </HeaderText>
        <Text sizing="sm" appearance="subdued">
          Open a patient to see their care opportunities and today's agenda.
        </Text>
      </VerticalFlexbox>
      {ehrSearchErrors}
      <DataTable<PatientRow, unknown>
        columns={columns}
        data={rows}
        enablePagination={false}
      />
      {(hasPrevPage || hasNextPage) && onNextPage && onPrevPage && (
        <HorizontalFlexbox
          alignItems="center"
          justifyContent="flex-end"
          gap="xs"
          className="py-1"
        >
          <Button
            variant="outline"
            size="sm"
            onClick={onPrevPage}
            disabled={!hasPrevPage}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onNextPage}
            disabled={!hasNextPage}
          >
            Next
          </Button>
        </HorizontalFlexbox>
      )}
    </VerticalFlexbox>
  );
};
